import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { SydFilterContext } from "src/components/Common/UI/SydFilter/SydFilterContext";
import { format } from 'date-fns';
import { useTranslation } from "react-i18next";
import { convertApiHeaderToReactTableHeader } from "src/components/Common/UI/DndTable/UtilFunctions";
import { useGetAllPhoneLines } from "../api/getAllPhoneLines";
import { useGetPhoneLineDownloadCsv } from "../api/getPhoneLineDownloadCsv";
import { useGetPhoneLineDownloadExcel } from "../api/getPhoneLineDownloadExcel";

// All your logic is here
export const usePhoneLinesDataViewLogic = () => {
  const { activeFilters } = useContext(SydFilterContext);
  const [modalEvent, setModalEvent] = useState<IEvent>({action: "",selectedRows: [],});
  const [selectedRowsOfTable, setSelectedRowsOfTable] = useState<IGetPhoneLine["data"]>([]);
  const getAllPhoneLinesQuery = useGetAllPhoneLines({data: activeFilters as IPhoneLineSearchForm});
  const downloadAnchorRef = useRef<HTMLAnchorElement>(null);
  const phoneLineDownloadCsvMutation = useGetPhoneLineDownloadCsv({data: activeFilters as IPhoneLineSearchForm});
  const phoneLineDownloadExcelMutation = useGetPhoneLineDownloadExcel({data: activeFilters as IPhoneLineSearchForm});
  const { t } = useTranslation();

  const loadingDone = [getAllPhoneLinesQuery].every(({ isFetching }) => !isFetching);
  const errorData = [getAllPhoneLinesQuery].some(({ isError }) => isError);

  let canDownloadCsv = false;
  let canDownloadExcel = false;

  if(getAllPhoneLinesQuery.data){
    canDownloadCsv = getAllPhoneLinesQuery.data?.data.length > 0;
    canDownloadExcel = getAllPhoneLinesQuery.data?.data.length > 0;
  }

  const dataTypes = [
    { id: 0, apiType: "Default", frontType: "", frontDisplayFormat: "" },
    { id: 1, apiType: "Integer", frontType: "number", frontDisplayFormat: "" },
    { id: 2, apiType: "Double", frontType: "number", frontDisplayFormat: "" },
    { id: 3, apiType: "Date", frontType: "date", frontDisplayFormat: "YYYY-MM-DD", sourceDisplayFormat: "YYYY-MM-DDTHH:mm:ss" },
    { id: 4, apiType: "String", frontType: "string", frontDisplayFormat: "" },
    { id: 5, apiType: "yyyyMMdd", frontType: "date", frontDisplayFormat: "YYYY-MM-DD", sourceDisplayFormat: "YYYYMMDD" },
    { id: 6, apiType: "Boolean", frontType: "boolean", frontDisplayFormat: "" },
    { id: 7, apiType: "IntegerTime", frontType: "date", frontDisplayFormat: "HH:mm:ss", sourceDisplayFormat: "seconds" },
    { id: 8, apiType: "yyyyMM", frontType: "date", frontDisplayFormat: "MMM YYYY", sourceDisplayFormat: "YYYYMM" }
  ];

  const data = getAllPhoneLinesQuery.data ? getAllPhoneLinesQuery.data.data : [];
  const apiTotalRecords = getAllPhoneLinesQuery.data ? getAllPhoneLinesQuery.data.totalRercords : 0;
  const visibleTotalRecords = getAllPhoneLinesQuery.data ? getAllPhoneLinesQuery.data.data.length : 0;

  const numberOfRowsTitle = visibleTotalRecords < apiTotalRecords
    ? `Τηλεφωνικές γραμμές. [Συνολικές εγγραφές: ${apiTotalRecords}]`
    : 'Τηλεφωνικές γραμμές.';

  const columnData = [
    {
      columnName: "id",
      columnDisplayName: "Γραμμή",
      visible: true,
      columnOrder: 1,
      isDraggable: false,
      columnDataType: "String",
    },
    {
      columnName: "phoneNumber",
      columnDisplayName: "Αριθμός",
      visible: true,
      columnOrder: 2,
      isDraggable: false,
      columnDataType: "String",
    },
    {
      columnName: "provider",
      columnDisplayName: "Πάροχος",
      visible: true,
      columnOrder: 3,
      isDraggable: false,
      columnDataType: "String",
    },
    {
      columnName: "unit",
      columnDisplayName: "Μονάδα",
      visible: true,
      columnOrder: 4,
      isDraggable: false,
      columnDataType: "String",
    },
    {
      columnName: "descline",
      columnDisplayName: "Περιγραφή",
      visible: true,
      columnOrder: 5,
      isDraggable: false,
      columnDataType: "String",
    },
    {
      columnName: "comments",
      columnDisplayName: "Σχόλια",
      visible: true,
      columnOrder: 6,
      isDraggable: false,
      columnDataType: "String",
    },
    {
      columnName: "removalDate",
      columnDisplayName: "Ημερομηνία Διαγραφής",
      visible: true,
      columnOrder: 7,
      isDraggable: false,
      columnDataType: "Date",
    }
  ]

  const columns = useMemo(() => {
    console.log("columnData",columnData)
    return convertApiHeaderToReactTableHeader(
      columnData ,
      dataTypes
    );
  }, []);

  const closeActionModal = () => {
    setModalEvent({ action: "", selectedRows: [] });
  };

  const getModalTitleByAction = (): string => {
    let title = "";
    switch (modalEvent.action) {
      case "create":
        title = t("Δημιουργία Τηλεφωνικής γραμμής");
        break
      case "edit":
        title = t("Επεξεργασία Τηλεφωνικής γραμμής");
        break
      case "delete":
        title = t("Διαγραφή Τηλεφωνικής γραμμής");
        break;
      case "bulkdelete":
        title = t("Μαζική Διαγραφή Τηλεφωνικής γραμμής");
        break;
      default:
        break;
        
    }
    return title;
  };

  const downloadFile = (blobString: string, reportName: string, fileExtension: string) => {
    if (downloadAnchorRef.current) {
      const recordingsBlob = new Blob([blobString], { type: 'application/octet-stream' });
      const recordingsBlobURL = URL.createObjectURL(recordingsBlob);

      const currentDate: Date = new Date();
      const nowDateFormated: string = format(currentDate, 'yyyy-MM-dd_HH-mm-ss');
      const fileName:string = `${reportName}_${nowDateFormated}.${fileExtension}`;

      downloadAnchorRef.current.href = recordingsBlobURL;
      downloadAnchorRef.current.download = fileName;
      downloadAnchorRef.current.click();
    }
  }

  const DownloadCsv = async () => {
    phoneLineDownloadCsvMutation.mutateAsync({data: activeFilters as IPhoneLineSearchForm})
    .then((apiRecordingsCsvResponse) => {
      downloadFile(apiRecordingsCsvResponse, "PhoneLine", "csv");
    }).catch((error) => {
      console.log(error)
    }) 
  }

  const DownloadExcel = async () => {
    phoneLineDownloadExcelMutation.mutateAsync({data: activeFilters as IPhoneLineSearchForm})
    .then((apiRecordingsExcelResponse) => {
      downloadFile(apiRecordingsExcelResponse, "PhoneLine", "xlsx");
    }).catch((error) => {
      console.log(error)
    })
  }

  const actionEvent = async (event: IEvent) => {
    switch (event.action) {
      case "downloadCsv":
        await DownloadCsv();
        break;
      case "downloadExcel":
        await DownloadExcel();
        break;
      default:
        const selectedRows: number[] = selectedRowsOfTable[0]
        ? [...[selectedRowsOfTable[0].id]]
        : [];
        setModalEvent({ action: event.action, selectedRows: selectedRows });
    }
   
  };

  useEffect(() => {
    setSelectedRowsOfTable([]);
    getAllPhoneLinesQuery.refetch();
  }, [activeFilters]);

  return {
    data,
    columns,
    loadingDone,
    selectedRowsOfTable,
    setSelectedRowsOfTable,
    t,
    modalEvent,
    actionEvent,
    closeActionModal,
    getModalTitleByAction,
    numberOfRowsTitle,
    visibleTotalRecords,
    downloadAnchorRef,
    canDownloadCsv,
    canDownloadExcel
  };
};
